/**
 * Unlisted banks (TMMPDM-0112; tmm-api 0195 U1, YAML at `f7c843fb`, MM-D-152): the bank names members
 * typed that aren't in the institutions catalog. The server groups the open requests by typed name and
 * country: each row is the typed name, the country in words, how many requests it stands for and the
 * latest one's time; never who typed it. Newest first, at most 200 rows and no paging: a full 200 says
 * "Showing the 200 newest."
 *
 * - Add to catalog…: name (the typed one), country (prefilled), city (optional) and kind; it sends
 *   `{name, country, city?, kind}`, tmm-api creates an `ADMIN` institution, closes the row's requests
 *   and answers the institution, whose name the toast says.
 * - Dismiss…: a confirm; the row's requests are closed.
 *
 * Errors stay in the dialog (TMM-B4); `INSTITUTION_REQUEST_STATE` is "This request has already been
 * handled." A refusal that means the row moved on re-reads the list behind the dialog; on success the
 * list re-reads. Until tmm-api deploys 0195 U1 the list answers a bare 404: "Unlisted banks aren't
 * available yet." with no retry.
 *
 * TMMPDM-0114 (tmm-api 0196 item 37n, spec at `10661502`): an add refused `INSTITUTION_EXISTS` says
 * so with the dialog's country in words, and the primary action becomes Dismiss request, the row's
 * own dismiss. Editing the name or the country clears the refusal and restores Add; the matched
 * `institutionId` is never shown.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { adminApi } from '@/api/tmm/admin';
import { institutionExists, isActingAdminRevoked, isValidationFailed } from '@/api/errors';
import {
  INSTITUTION_REQUESTS_MAX,
  type InstitutionKind,
  type InstitutionRequestList,
  type InstitutionRequestRow,
} from '@/api/tmm/types';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  RowActionsMenu,
  SubmitButton,
  useNotify,
} from '@/components/page';
import { stickyTableContainerSx } from '@/components/ui';
import { formatCountry, formatInstant } from '@/format';
import {
  CITY_MAX,
  INSTITUTION_KINDS,
  INSTITUTION_NAME_MAX,
  actionError,
  institutionKindLabel,
  isNotDeployed,
  isStaleRow,
  notAvailable,
} from './knowledge';

const headerCellSx = { fontWeight: 600 } as const;
const COUNTRY_CODE = /^[A-Za-z]{2}$/;

type Dialog = { kind: 'add' | 'dismiss'; row: InstitutionRequestRow } | { kind: 'none' };

export function UnlistedBanksTab() {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [response, setResponse] = useState<{
    token: number | null;
    result: InstitutionRequestList | null;
    failure: unknown;
  }>({ token: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listInstitutionRequests()
      .then((result) => {
        if (!cancelled) setResponse({ token: reloadToken, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled)
          setResponse({ token: reloadToken, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const current = response.token === reloadToken;
  // The last good read stays on screen while a re-read after a write is in flight.
  const failure = current ? response.failure : null;
  const rows = response.result?.requests ?? [];
  const loading = response.result === null && !current;
  const close = () => setDialog({ kind: 'none' });
  const stale = () => setReloadToken((n) => n + 1);
  const done = (message: string) => {
    close();
    notify.success(message);
    setReloadToken((n) => n + 1);
  };
  // The row's Dismiss… and the add dialog's Dismiss request end the same way.
  const dismissed = () => done(t('knowledge.banks.dismiss.done', 'Request dismissed.'));

  if (failure !== null && isNotDeployed(failure)) {
    return <EmptyState surface title={notAvailable(t, 'banks')} />;
  }
  if (failure) {
    return (
      <ErrorState
        error={failure}
        message={t('knowledge.banks.loadError', 'Could not load the unlisted banks.')}
        {...(isActingAdminRevoked(failure) ? {} : { onRetry: () => setReloadToken((n) => n + 1) })}
      />
    );
  }
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('common.loading', 'Loading')}
      </Typography>
    );
  }

  return (
    <Box>
      {rows.length === 0 ? (
        <EmptyState surface title={t('knowledge.banks.empty', 'No unlisted banks to review.')} />
      ) : (
        <>
          {rows.length === INSTITUTION_REQUESTS_MAX ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('knowledge.banks.newest', 'Showing the {{max, number}} newest.', {
                max: INSTITUTION_REQUESTS_MAX,
              })}
            </Typography>
          ) : null}
          <TableContainer
            component={Paper}
            variant="outlined"
            role="region"
            aria-label={t('knowledge.banks.regionAria', 'Unlisted banks list')}
            aria-busy={current ? 'false' : 'true'}
            sx={stickyTableContainerSx}
          >
            <Table
              size="small"
              stickyHeader
              aria-label={t('knowledge.tab.banks', 'Unlisted banks')}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    {t('knowledge.banks.col.typedName', 'Typed name')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('knowledge.banks.col.country', 'Country')}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t('knowledge.banks.col.count', 'Requests')}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t('knowledge.banks.col.requestedAt', 'Last requested')}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t('knowledge.col.actions', 'Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.typedName}</TableCell>
                    <TableCell>{formatCountry(row.country, locale)}</TableCell>
                    <TableCell align="right">
                      {t('knowledge.banks.requests', '{{count, number}} requests', {
                        count: row.count,
                      })}
                    </TableCell>
                    <TableCell>{formatInstant(row.requestedAt, locale)}</TableCell>
                    <TableCell align="right">
                      <RowActionsMenu
                        name={row.typedName}
                        items={[
                          {
                            key: 'add',
                            label: t('knowledge.banks.add.open', 'Add to catalog…'),
                            onSelect: () => setDialog({ kind: 'add', row }),
                          },
                          {
                            key: 'dismiss',
                            label: t('knowledge.banks.dismiss.open', 'Dismiss…'),
                            destructive: true,
                            onSelect: () => setDialog({ kind: 'dismiss', row }),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <AddInstitutionDialog
        row={dialog.kind === 'add' ? dialog.row : null}
        onClose={close}
        onStale={stale}
        onDone={(name) =>
          done(t('knowledge.banks.add.done', '{{name}} added to the catalog.', { name }))
        }
        onDismissed={dismissed}
      />
      <DismissRequestDialog
        row={dialog.kind === 'dismiss' ? dialog.row : null}
        onClose={close}
        onStale={stale}
        onDone={dismissed}
      />
    </Box>
  );
}

interface DialogProps {
  row: InstitutionRequestRow | null;
  onClose: () => void;
  /** A refusal that means the row moved on: the list behind the dialog re-reads. */
  onStale: () => void;
}

function AddInstitutionDialog({
  row,
  onClose,
  onStale,
  onDone,
  onDismissed,
}: DialogProps & { onDone: (name: string) => void; onDismissed: () => void }) {
  const titleId = useId();
  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      {row !== null ? (
        <AddInstitutionForm
          row={row}
          titleId={titleId}
          onClose={onClose}
          onStale={onStale}
          onDone={onDone}
          onDismissed={onDismissed}
        />
      ) : null}
    </Dialog>
  );
}

function AddInstitutionForm({
  row,
  titleId,
  onClose,
  onStale,
  onDone,
  onDismissed,
}: {
  row: InstitutionRequestRow;
  titleId: string;
  onClose: () => void;
  onStale: () => void;
  onDone: (name: string) => void;
  /** Dismiss request, offered once the add is refused `INSTITUTION_EXISTS`, succeeded. */
  onDismissed: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [name, setName] = useState(row.typedName);
  const [country, setCountry] = useState(row.country);
  const [city, setCity] = useState('');
  const [kind, setKind] = useState<InstitutionKind | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // The country an add was refused INSTITUTION_EXISTS for; cleared by editing the name or country.
  const [existsIn, setExistsIn] = useState<string | null>(null);

  const dismiss = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.dismissInstitutionRequest(row.id);
      onDismissed();
    } catch (failure: unknown) {
      setError(
        actionError(
          failure,
          t,
          'banks',
          t('knowledge.banks.dismiss.error', "Couldn't dismiss the request."),
        ),
      );
      if (isStaleRow(failure)) onStale();
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const trimmedName = name.trim();
    const trimmedCountry = country.trim().toUpperCase();
    const trimmedCity = city.trim();
    const errors: Record<string, string> = {};
    if (trimmedName === '') errors['name'] = t('knowledge.banks.add.nameRequired', 'Enter a name.');
    if (!COUNTRY_CODE.test(trimmedCountry)) {
      errors['country'] = t(
        'knowledge.banks.add.countryInvalid',
        'Enter the two-letter country code, such as CR or US.',
      );
    }
    if (kind === '') errors['kind'] = t('knowledge.banks.add.kindRequired', 'Choose a kind.');
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0 || kind === '') return;
    setBusy(true);
    setError(null);
    try {
      const created = await adminApi.addInstitutionRequest(row.id, {
        name: trimmedName,
        country: trimmedCountry,
        ...(trimmedCity === '' ? {} : { city: trimmedCity }),
        kind,
      });
      onDone(created.name);
    } catch (failure: unknown) {
      if (institutionExists(failure) !== null) {
        setExistsIn(trimmedCountry);
      } else if (isValidationFailed(failure)) {
        setError(t('errors.field.rejected', "This value isn't accepted. Check it and try again."));
      } else {
        setError(
          actionError(
            failure,
            t,
            'banks',
            t('knowledge.banks.add.error', "Couldn't add the bank to the catalog."),
          ),
        );
        if (isStaleRow(failure)) onStale();
      }
    } finally {
      setBusy(false);
    }
  };

  const countryName = COUNTRY_CODE.test(country.trim())
    ? formatCountry(country, i18n.language)
    : null;

  return (
    <>
      <DialogTitle id={titleId}>
        {t('knowledge.banks.add.title', 'Add “{{name}}” to the catalog', { name: row.typedName })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            required
            label={t('knowledge.banks.add.name', 'Name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setFieldErrors(({ name: _name, ...rest }) => rest);
              setError(null);
              setExistsIn(null);
            }}
            error={fieldErrors['name'] !== undefined}
            helperText={
              fieldErrors['name'] ?? t('knowledge.banks.add.nameHint', 'As members should see it.')
            }
            slotProps={{ htmlInput: { maxLength: INSTITUTION_NAME_MAX } }}
          />
          <TextField
            required
            label={t('knowledge.banks.add.country', 'Country')}
            value={country}
            onChange={(event) => {
              setCountry(event.target.value);
              setFieldErrors(({ country: _country, ...rest }) => rest);
              setError(null);
              setExistsIn(null);
            }}
            error={fieldErrors['country'] !== undefined}
            helperText={fieldErrors['country'] ?? countryName ?? ' '}
            slotProps={{ htmlInput: { maxLength: 2, autoCapitalize: 'characters' } }}
          />
          <TextField
            label={t('knowledge.banks.add.city', 'City (optional)')}
            value={city}
            onChange={(event) => setCity(event.target.value)}
            slotProps={{ htmlInput: { maxLength: CITY_MAX } }}
          />
          <TextField
            select
            required
            label={t('knowledge.banks.add.kind', 'Kind')}
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as InstitutionKind);
              setFieldErrors(({ kind: _kind, ...rest }) => rest);
              setError(null);
            }}
            error={fieldErrors['kind'] !== undefined}
            helperText={fieldErrors['kind'] ?? ' '}
          >
            {INSTITUTION_KINDS.map((code) => (
              <MenuItem key={code} value={code}>
                {institutionKindLabel(code, t)}
              </MenuItem>
            ))}
          </TextField>
          {existsIn !== null ? (
            <Alert severity="error">
              {t(
                'knowledge.banks.add.exists',
                'Already listed. A bank with this name is already in the catalog for {{country}}. Dismiss this request instead.',
                { country: formatCountry(existsIn, i18n.language) },
              )}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        {existsIn !== null ? (
          <SubmitButton busy={busy} color="error" onClick={() => void dismiss()}>
            {t('knowledge.banks.add.dismissInstead', 'Dismiss request')}
          </SubmitButton>
        ) : (
          <SubmitButton busy={busy} onClick={() => void submit()}>
            {t('knowledge.banks.add.confirm', 'Add to catalog')}
          </SubmitButton>
        )}
      </DialogActions>
    </>
  );
}

function DismissRequestDialog({
  row,
  onClose,
  onStale,
  onDone,
}: DialogProps & { onDone: () => void }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async (target: InstitutionRequestRow) => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.dismissInstitutionRequest(target.id);
      onDone();
    } catch (failure: unknown) {
      setError(
        actionError(
          failure,
          t,
          'banks',
          t('knowledge.banks.dismiss.error', "Couldn't dismiss the request."),
        ),
      );
      if (isStaleRow(failure)) onStale();
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      open={row !== null}
      title={t('knowledge.banks.dismiss.title', 'Dismiss “{{name}}”?', {
        name: row?.typedName ?? '',
      })}
      consequence={t(
        'knowledge.banks.dismiss.consequence',
        'The {{count, number}} requests from {{country}} are closed. Nothing is added to the catalog.',
        { count: row?.count ?? 0, country: formatCountry(row?.country, i18n.language) },
      )}
      confirmLabel={t('knowledge.banks.dismiss.confirm', 'Dismiss')}
      destructive
      busy={busy}
      error={error}
      onConfirm={() => {
        if (row !== null) void confirm(row);
      }}
      onCancel={() => {
        setError(null);
        onClose();
      }}
    />
  );
}
